import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * RC10l · Cancel Unavailability two-step confirmation.
 *
 * Keyboard-dismissable. Confirms destructive deletion of an
 * `driver_availability_windows` row flagged `status=unavailable`.
 * The backend `DELETE /api/drivers/{driver}/availability-windows/
 * {window}` is already gated on `perm("drivers.availability")` and
 * clears `coverage_needed` on affected assignments.
 */
export default function CancelUnavailabilityConfirm({
  driverId,
  windowId,
  windowStartDate,
  windowEndDate,
  windowReason,
  busy,
  onCancel,
  onConfirm,
}) {
  // Two-step: user must tick the acknowledgement before Confirm enables.
  const [acknowledged, setAcknowledged] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-carbon/70 flex items-center justify-center px-4"
      data-testid="cancel-unavailability-confirm"
      onClick={() => { if (!busy) onCancel(); }}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-md w-full max-w-md border border-coach-silver shadow-xl"
      >
        <div className="bg-critical-red text-white px-4 py-3 flex items-center justify-between">
          <div className="text-[14px] font-medium">Cancel Unavailability</div>
          <button
            type="button"
            onClick={() => { if (!busy) onCancel(); }}
            data-testid="cancel-unavailability-close"
            className="text-coach-silver hover:text-white"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3 text-[13px] text-carbon">
          <div>
            This will remove the Unavailable window
            {" "}<span className="font-mono tabular text-[12px]">
              {windowStartDate} → {windowEndDate}
            </span>
            {" "}from this driver and clear the related{" "}
            <span className="text-amber-hold font-medium">Coverage Needed</span> warning on any
            affected Tour.
          </div>
          {windowReason && (
            <div className="text-[12px] text-steel">
              Reason on file: <span className="text-carbon">{windowReason}</span>
            </div>
          )}
          <label className="flex items-start gap-2 text-[12.5px] text-carbon border-t border-coach-silver pt-3">
            <input
              type="checkbox"
              checked={acknowledged}
              disabled={busy}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="cancel-unavailability-ack"
              className="mt-0.5"
            />
            <span>
              I understand this cannot be undone from this dialog. The driver will become
              available-by-default for this date range.
            </span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-coach-silver bg-road-white rounded-b-md">
          <button
            type="button"
            onClick={() => { if (!busy) onCancel(); }}
            disabled={busy}
            data-testid="cancel-unavailability-cancel"
            className="px-3 py-1.5 text-[12.5px] border border-coach-silver rounded-sm text-steel hover:bg-white"
          >
            Keep Unavailability
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || !acknowledged}
            data-testid="cancel-unavailability-confirm"
            className="px-3 py-1.5 text-[12.5px] bg-critical-red text-white rounded-sm hover:bg-critical-red/90 disabled:opacity-40"
          >
            {busy ? "Cancelling…" : "Cancel Unavailability"}
          </button>
        </div>
      </div>
    </div>
  );
}
